import React, { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Trash2, Send, Loader2, MessageCircle } from 'lucide-react';
import { postsAPI, getErrorMessage } from '../../services/api';

interface InternalComment {
  id: string;
  user_id: string;
  user_name: string;
  content: string;
  created_at: string;
  updated_at: string | null;
}

interface InternalCommentsProps {
  postId: string;
  currentUserId: string;
}

function getInitials(name: string): string {
  return name
    .split(' ')
    .slice(0, 2)
    .map((word) => word[0].toUpperCase())
    .join('');
}

function formatRelativeTime(date: string): string {
  const now = new Date();
  const created = new Date(date);
  const seconds = Math.floor((now.getTime() - created.getTime()) / 1000);

  if (seconds < 60) {
    return 'just now';
  }
  if (seconds < 3600) {
    const minutes = Math.floor(seconds / 60);
    return `${minutes}m ago`;
  }
  if (seconds < 86400) {
    const hours = Math.floor(seconds / 3600);
    return `${hours}h ago`;
  }
  const days = Math.floor(seconds / 86400);
  return `${days}d ago`;
}

export function InternalComments({ postId, currentUserId }: InternalCommentsProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [commentInput, setCommentInput] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const { data: commentsData, isLoading, error } = useQuery({
    queryKey: ['post', postId, 'comments'],
    queryFn: () =>
      postsAPI
        .listComments(postId)
        .then((res) => (res.data.comments as InternalComment[]) || []),
    staleTime: 30_000,
  });

  const comments = commentsData || [];

  const addCommentMutation = useMutation({
    mutationFn: (content: string) =>
      postsAPI.addComment(postId, { content }).then((res) => res.data),
    onSuccess: () => {
      setCommentInput('');
      queryClient.invalidateQueries({ queryKey: ['post', postId, 'comments'] });
    },
    onError: (err: unknown) => {
      toast.error(getErrorMessage(err, t('comments_add_error', 'Failed to add comment')));
    },
  });

  const deleteCommentMutation = useMutation({
    mutationFn: (commentId: string) =>
      postsAPI.deleteComment(postId, commentId).then((res) => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['post', postId, 'comments'] });
    },
    onError: (err: unknown) => {
      toast.error(getErrorMessage(err, t('comments_delete_error', 'Failed to delete comment')));
    },
  });

  const handleAddComment = async () => {
    const trimmed = commentInput.trim();
    if (!trimmed) {
      return;
    }
    addCommentMutation.mutate(trimmed);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      handleAddComment();
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200/60 p-5 shadow-sm shadow-slate-200/40 mt-4">
      {/* Header — matches composer card label style */}
      <div className="flex items-center gap-2 mb-3">
        <MessageCircle className="h-4 w-4 text-amber-500" />
        <span className="text-xs font-bold text-gray-400 uppercase tracking-widest">
          {t('comments_label', 'Team Notes')}
        </span>
        <span className="text-[10px] bg-amber-50 text-amber-700 px-2 py-0.5 rounded-full font-semibold border border-amber-200">
          {t('comments_visibility', 'Internal only')}
        </span>
      </div>

      {/* Comments list */}
      <div className="mb-3 max-h-64 space-y-3 overflow-y-auto">
        {isLoading && (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
          </div>
        )}

        {error && (
          <div className="rounded-lg bg-red-50 p-3 text-sm text-red-600">
            {t('comments_error', 'Failed to load comments')}
          </div>
        )}

        {comments.length === 0 && !isLoading && !error && (
          <p className="py-3 text-center text-sm text-gray-400">
            {t('comments_empty', 'No comments yet. Add a note for your team.')}
          </p>
        )}

        {comments.map((comment) => {
          const isOwnComment = comment.user_id === currentUserId;
          const initials = getInitials(comment.user_name);
          const relativeTime = formatRelativeTime(comment.created_at);

          return (
            <div key={comment.id} className="flex gap-3 group">
              <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-slate-100 text-[10px] font-semibold text-slate-600">
                {initials}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-2">
                  <span className="text-sm font-medium text-gray-800">
                    {comment.user_name}
                  </span>
                  <span className="text-[11px] text-gray-400">{relativeTime}</span>
                  {isOwnComment && (
                    <button
                      onClick={() => {
                        if (window.confirm(t('comments_delete_confirm', 'Delete this comment?'))) {
                          deleteCommentMutation.mutate(comment.id);
                        }
                      }}
                      disabled={deleteCommentMutation.isPending}
                      className="opacity-0 group-hover:opacity-100 transition-opacity ml-auto text-gray-400 hover:text-red-500 disabled:opacity-50"
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  )}
                </div>
                <p className="mt-0.5 break-words text-sm text-gray-600 leading-relaxed">
                  {comment.content}
                </p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Input — matches composer textarea style */}
      <div className="flex gap-2 items-end">
        <textarea
          ref={textareaRef}
          value={commentInput}
          onChange={(e) => setCommentInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={t('comments_placeholder', 'Add a note for your team...')}
          rows={1}
          className="flex-1 h-10 p-3 border border-slate-100 rounded-xl focus:ring-2 focus:ring-amber-500/20 focus:border-amber-300/50 resize-none text-sm leading-relaxed transition-all duration-200 bg-slate-50/30 focus:bg-white"
        />
        <button
          onClick={handleAddComment}
          disabled={!commentInput.trim() || addCommentMutation.isPending}
          className="flex items-center gap-1.5 h-10 rounded-xl bg-slate-100 px-4 text-sm font-medium text-slate-600 hover:bg-slate-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          {addCommentMutation.isPending ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Send className="h-3.5 w-3.5" />
          )}
          {t('comments_submit', 'Send')}
        </button>
      </div>
    </div>
  );
}
